import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, Loader2, Compass } from "lucide-react";
import { SharedCaseComparison } from "./SharedCaseComparison";

export default function SharedComparison() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/shared-comparison/${token}`);
        setState({ loading: false, error: null, data });
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "This comparison link is not available.", data: null });
      }
    })();
  }, [token]);

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-[#0B192C]"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B192C] text-white p-6" data-testid="shared-error">
      <div className="text-center"><p className="font-semibold text-lg">{state.error}</p><p className="text-sm text-white/60 mt-2">Ask your licensed insurance agent to resend the link.</p></div>
    </div>
  );

  if (state.data.kind === "case") return <SharedCaseComparison token={token} data={state.data} />;
  const cmp = state.data.comparison || {};
  const cands = cmp.candidates || [];
  const rows = (cmp.rows || []).filter((r) => (r.current && r.current.text !== "not available") || (r.candidates || []).some((c) => c.text !== "not available"));
  const label = (b) => (b.plan_name || b.display_id || "").slice(0, 26);

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="shared-comparison">
      <div className="bg-[#0B192C] text-white px-6 py-6">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          <Compass className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <p className="font-display font-bold text-xl">Your 2027 plan comparison</p>
            <p className="text-sm text-white/70">Prepared by {state.data.advisor_name}</p>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-6">
        {cands.some((b) => (b.highlights || []).length > 0) && (
          <div className="space-y-2 mb-5" data-testid="shared-highlights">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8F98]">{cmp.current ? "What changes vs your current plan" : "Standout benefits"}</p>
            {cands.map((b) => ((b.highlights || []).length > 0 && (
              <div key={b.display_id} className="rounded-xl bg-white border border-[#E7E4DB] p-3">
                <p className="text-xs font-semibold text-[#0B192C] mb-2">{label(b)}</p>
                <div className="flex flex-wrap gap-2">
                  {b.highlights.map((h) => (
                    <span key={h.key} className={`text-xs rounded-full px-3 py-1 font-semibold inline-flex items-center gap-1.5 border ${h.tone === "positive" ? "bg-[#ECFDF3] text-[#067647] border-[#ABEFC6]" : h.tone === "negative" ? "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" : "bg-white text-[#0B192C] border-[#D7D4CB]"}`}>
                      <span className="opacity-60 font-medium">{h.label}</span><span>{h.delta_text || h.value_text}</span>
                    </span>
                  ))}
                </div>
              </div>
            )))}
          </div>
        )}
        <div className="rounded-xl border border-[#E7E4DB] bg-white p-4 overflow-x-auto">
          <p className="text-sm font-semibold text-[#0B192C] flex items-center gap-1.5 mb-3"><ShieldCheck className="w-4 h-4 text-[#1E3E62]" /> Supported 2027 plan facts{cmp.current ? " vs your current plan" : ""}</p>
          <table className="w-full text-xs border-collapse" data-testid="shared-table">
            <thead>
              <tr className="text-left text-[#8A8F98]">
                <th className="py-2 pr-3 font-medium">Benefit</th>
                {cmp.current && <th className="py-2 pr-3 font-medium">{(cmp.current.plan_name || "Current").slice(0, 24)}</th>}
                {cands.map((b) => <th key={b.display_id} className="py-2 pr-3 font-medium">{label(b)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-[#E7E4DB]">
                  <td className="py-2 pr-3 text-[#5B6670]">{r.label}</td>
                  {cmp.current && <td className="py-2 pr-3 text-[#0B192C]">{r.current?.text || "not available"}</td>}
                  {(r.candidates || []).map((c) => <td key={c.display_id} className="py-2 pr-3 text-[#1E3E62]">{c.text}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#8A8F98] mt-4 max-w-2xl">This is factual plan data, not a ranking, recommendation, or personalized cost estimate. Provider networks and prescription drug coverage aren't included — your licensed insurance agent can verify your doctors and medications.</p>
      </div>
    </div>
  );
}
